import { useCallback, useState } from "react";
import type { Draft, DraftGridLens, DraftProblem } from "@heraion/shared";

// Where the phone editor is looking (#336): the day, the lens the day is
// read through («Exercises» or «Per round»), the round under the heatmap's
// cursor (also the lens's round), the exercise open in the round ladder and
// the round expanded in it. Indices are clamped against the live draft on every read,
// so a removed day, round or exercise (or an undo that brings one back)
// never leaves a cursor pointing at nothing. The exercise is tracked by id:
// a move or a delete above it does not swap it for another.
// On a wide screen (slice 6) the exercise has a pane of its own, never empty
// while the day has one: with `pinFirst` the day's first exercise opens when
// none is (mock `day()`), by id like any other, so a move keeps it in front.
// There the day has no visible «Per round» lens: the lens stays «Exercises»,
// and a heatmap cell opens the exercise on its round instead. The grid's lens
// lives here too.
/** How the day reads: one row per exercise over every round, or one round. */
export type V3Lens = "exercises" | "round";
/** Where a view opens: a day and the cursor's round. */
export type V3Start = { day: number; round: number };

export function useV3Navigation(draft: Draft, pinFirst = false, start?: V3Start) {
  const [day, setDay] = useState(start?.day ?? 0);
  const [lens, setLens] = useState<V3Lens>("exercises");
  const [gridLens, setGridLens] = useState<DraftGridLens>("effort");
  const [round, setRound] = useState(start?.round ?? 0);
  const [openSlotId, setOpenSlotId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const dayIdx = Math.max(0, Math.min(day, draft.sessions.length - 1));
  const roundIdx = Math.max(0, Math.min(round, draft.cycleWeeks - 1));
  const slots = draft.sessions[dayIdx]?.slots ?? [];
  const found = openSlotId === null ? -1 : slots.findIndex((s) => s.id === openSlotId);
  const pinned = found < 0 && pinFirst ? slots[0] : undefined;
  // Derived state, set while rendering: React renders again before painting.
  if (pinned && openSlotId !== pinned.id) setOpenSlotId(pinned.id);
  const openSlotIdx = found >= 0 ? found : pinned ? 0 : null;
  // Crossing to a wide screen with «Per round» on: back to «Exercises».
  if (pinFirst && lens === "round") setLens("exercises");
  const expandedRound =
    openSlotIdx !== null && expanded !== null && expanded < draft.cycleWeeks ? expanded : null;

  const selectDay = useCallback((d: number) => {
    setDay(d);
    setOpenSlotId(null);
    setExpanded(null);
  }, []);

  // A heatmap cell: that day and round, read through the «Per round» lens.
  // With an exercise open on the same day, the round opens in its ladder
  // instead (3a): the exercise stays in front.
  const selectCell = useCallback(
    (d: number, w: number) => {
      setRound(w);
      // Wide: that day, its open exercise (or its first) on round w.
      if (pinFirst) {
        if (d !== dayIdx || openSlotIdx === null) {
          if (d !== dayIdx) selectDay(d);
          const first = draft.sessions[d]?.slots[0];
          if (first) setOpenSlotId(first.id);
        }
        setExpanded(w);
        return;
      }
      if (d === dayIdx && openSlotIdx !== null) {
        setExpanded(w);
        return;
      }
      if (d !== dayIdx) selectDay(d);
      setLens("round");
    },
    [dayIdx, openSlotIdx, selectDay, pinFirst, draft.sessions],
  );

  const openExercise = useCallback((id: string) => {
    setOpenSlotId(id);
    setExpanded(null);
  }, []);

  // A row of the «Per round» lens: the exercise, with that round open.
  const openExerciseAt = useCallback((id: string, w: number) => {
    setOpenSlotId(id);
    setRound(w);
    setExpanded(w);
  }, []);

  // The lens's round chips, and the round menu's moves: the cursor follows.
  const selectRound = useCallback((w: number) => setRound(w), []);

  const closeExercise = useCallback(() => {
    setOpenSlotId(null);
    setExpanded(null);
  }, []);

  // One round open at a time: tapping the open one closes it.
  const toggleRound = useCallback((w: number) => {
    setRound(w);
    setExpanded((cur) => (cur === w ? null : w));
  }, []);

  // A save problem (#336, brief §9): its day, its exercise, its round open in
  // the ladder. A day-level one shows the day; a program-level one (the name)
  // has nowhere to go and stays in words at the top.
  const goToProblem = useCallback((p: DraftProblem) => {
    if (p.sIdx === undefined) return;
    setDay(p.sIdx);
    setOpenSlotId(p.slotId ?? null);
    if (p.week !== undefined) setRound(p.week);
    setExpanded(p.slotId !== undefined && p.week !== undefined ? p.week : null);
  }, []);

  return {
    dayIdx,
    lens,
    setLens,
    gridLens,
    setGridLens,
    roundIdx,
    openSlotIdx,
    expandedRound,
    selectDay,
    selectCell,
    openExercise,
    openExerciseAt,
    selectRound,
    closeExercise,
    toggleRound,
    goToProblem,
  };
}

export type V3Navigation = ReturnType<typeof useV3Navigation>;
